import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  shops: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    setShops: (state, action) => {
      state.shops = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setShops, setTotalCount, setTotalPages, setLoading, setError } = shopSlice.actions;

// Get shops list (paginated & searchable)
export const getShops = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const assigned_to = filters.assigned_to || '';
    const corporation_id = filters.corporation_id || '';
    const cityId = filters.cityId || '';
    const stateId = filters.stateId || '';
    const params = { page, limit, search };
    if (assigned_to) params.assigned_to = assigned_to;
    if (corporation_id) params.corporation_id = corporation_id;
    if (cityId) params.cityId = cityId;
    if (stateId) params.stateId = stateId;
    const response = await axiosInstance.get('/admin/shop/list', { params });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setShops(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setShops([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch shops';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setShops([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch shops';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single shop details (for edit prefill)
export const getShopDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/shop/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch shop details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch shop details';
    return { status: false, message: errorMsg };
  }
};

// Add new shop
export const addShop = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/shop/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add shop' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add shop';
    return { status: false, message: errorMsg };
  }
};

// Update shop details
export const updateShop = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/shop/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update shop' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update shop';
    return { status: false, message: errorMsg };
  }
};

// Delete shop
export const deleteShop = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/shop/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete shop' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete shop';
    return { status: false, message: errorMsg };
  }
};

// Import shops from excel file (form-data key: file)
export const importShops = (file) => async () => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post('/admin/shop/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to import shops' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to import shops';
    return { status: false, message: errorMsg };
  }
};

// Export shops data
export const exportShops = (filters) => async () => {
  try {
    const params = {
      search: filters?.search || '',
      corporation_id: filters?.corporation_id || '',
      cityId: filters?.cityId || '',
      stateId: filters?.stateId || '',
      assigned_to: filters?.assigned_to || '',
    };
    const response = await axiosInstance.get('/admin/shop/export', {
      params,
      responseType: 'blob',
    });

    if (response.data && response.data.type === 'application/json') {
      const text = await response.data.text();
      const json = JSON.parse(text);
      if (json.status === false) {
        return { status: false, message: json.message || 'Failed to export shops' };
      }
    }

    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'shops_export.xlsx';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) fileName = match[1];
    }
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { status: true };
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to export shops';
    return { status: false, message: errorMsg };
  }
};

export default shopSlice.reducer;
